import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface FormSectionProps {
  title: string;
  icon: React.ElementType;
  isComplete?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function FormSection({
  title,
  icon: Icon,
  isComplete,
  defaultOpen = false,
  children,
}: FormSectionProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Icon className="h-4 w-4 text-slate-600" />
              </div>
              <span className="text-base font-semibold text-slate-900">{title}</span>
            </div>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors',
                  isComplete
                    ? 'bg-green-500 border-green-500'
                    : 'border-slate-300 bg-white'
                )}
              >
                {isComplete && <Check className="h-3 w-3 text-white" />}
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 text-slate-400 transition-transform duration-200',
                  isOpen && 'rotate-180'
                )}
              />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 pt-2 border-t border-slate-100">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
